import { Utils } from './../../../helpers/utils.helper'
import { Store } from '@ngxs/store'
import { QuoteMetaState } from '../../quote/quote.meta.state'
import {
  IWarning,
  ProductTypes,
  WarningActions,
  WarningReferences,
} from '../smart-suggestion.model'
import { Injectable } from '@angular/core'
import { SmartSuggestionMetaState } from '../smart-suggestion.meta.state'
import { RemoveWarningAction } from '../smart-suggestion.meta.actions'
import { UpdateQuoteItemQuantityAction } from 'src/store/quote/quote.meta.actions'
import { BracketTypeEnum } from 'src/enums/bracket.enum'

interface BracketsProduct {
  item: string
  id: string
  quantity: number
  title: string
  description: string
  sapCode: string
}

interface BracketVariables {
  possibleRails: any[]
  possibleStructures: any[]
  needRailsMeters: number
  needModulesSupportQuantity: number
  supportRails: any[]
}

interface CcmSoilBracketsComposition {
  products: BracketsProduct[]
  panels?: number
  panelsPerBoard?: number[]
  sizing: 'A' | 'B'
}

interface CalculateCcmBSizing {
  panelsPerBoard: number[]
  products: BracketsProduct[]
}

interface CalculateCcmASizing {
  panels: number
  products: BracketsProduct[]
}

interface OnlyStructuresCombinations {
  possibleStructures: any[]
  needModulesSupportQuantity: number
  selectedStructure: any
}

interface OnlyRailsCombinations {
  possibleRails: any[]
  needRailsMeters: number
  selectedRail: any
}

type RailComplementCombination = BracketVariables & {
  selectedStructure: any
  selectedRail: any
}

@Injectable({ providedIn: 'root' })
export class BracketsHelper {
  constructor(
    private store: Store,
    private utils: Utils,
  ) {}

  findBracketsWarningReasons(): IWarning[] {
    const warnings = this.store.selectSnapshot(
      SmartSuggestionMetaState.getWarnings,
    )
    const bracketsWarnings = warnings.filter(
      (warning) =>
        warning.reference ===
        WarningReferences.PANEL_X_BRACKETS_MISSING_REQUIRED_BRACKETS,
    )
    return bracketsWarnings
  }

  validateBracketsConstraints() {
    // INFO: BYD-1392
    return []
    const {
      possibleRails,
      possibleStructures,
      needRailsMeters,
      needModulesSupportQuantity,
    } = this.getBracketsVariables() || {}
    const selectedBrackets = this.store.selectSnapshot(
      QuoteMetaState.getSelectedBrackets,
    )
    const foundWarnings = this.findBracketsWarningReasons()
    if (
      !possibleRails ||
      !possibleStructures ||
      !needRailsMeters ||
      !needModulesSupportQuantity ||
      selectedBrackets.length === 0
    ) {
      if (foundWarnings.length > 0) {
        this.store.dispatch(new RemoveWarningAction(foundWarnings[0].reference))
      }
      return []
    }
    const { totalModulesSupportQuantity, railsMetersQuantity } =
      this.getBracketsAmountValues(selectedBrackets)
    const isLessRailsMeters = railsMetersQuantity < needRailsMeters
    const isLessModulesSupportQuantity =
      totalModulesSupportQuantity < needModulesSupportQuantity
    const hasMiniRails = selectedBrackets.some((bracket) =>
      bracket.equipment.toUpperCase().includes('MINI-TRILHO'),
    )
    if (
      ((possibleRails.length > 0 && isLessRailsMeters) ||
        (possibleStructures.length > 0 && isLessModulesSupportQuantity)) &&
      !hasMiniRails
    ) {
      const content = `${this.utils.getTranslatedKey(
        'The brackets you selected are not enough to support the panels you selected, please select the necessary brackets for your panels before proceeding to checkout.',
      )}`
      const reason = {
        content,
        isRestrictive: true,
        lockedTabs: [7],
        options: [
          {
            actions: [WarningActions.SEE_SUGGESTIONS],
            title: 'See Suggestions',
          },
        ],
        reference: WarningReferences.PANEL_X_BRACKETS_MISSING_REQUIRED_BRACKETS,
      }
      return [reason]
    }
    if (foundWarnings.length > 0) {
      this.store.dispatch(new RemoveWarningAction(foundWarnings[0].reference))
    }
    return []
  }

  getBracketsVariables(): BracketVariables {
    const brackets = this.getFilteredBrackets()
    const selectedPanel = this.store.selectSnapshot(
      QuoteMetaState.getSelectedPanel,
    )
    if (brackets.length === 0 || !selectedPanel) {
      return
    }
    const needRailsMeters = selectedPanel?.quantity * 1200 * 2
    const possibleRails = brackets.filter(
      (bracket) =>
        bracket.bracketType === 'RAILS' && bracket?.railsMetersQuantity > 1200,
    )
    const needModulesSupportQuantity = selectedPanel?.quantity
    const possibleStructures = brackets.filter(
      (bracket) =>
        bracket.bracketType === BracketTypeEnum.STRUCTURE &&
        bracket?.modulesSupportQuantity > 0,
    )
    const supportRails = brackets.filter(
      (bracket) =>
        bracket.bracketType === 'RAILS' &&
        bracket?.railsMetersQuantity === 1200,
    )

    return {
      possibleRails,
      possibleStructures,
      needRailsMeters,
      needModulesSupportQuantity,
      supportRails,
    }
  }

  getBracketsSuggestions() {
    const tabFilters = this.store.selectSnapshot(QuoteMetaState.getTabFilters)

    if (tabFilters && tabFilters['brackets']?.roofType === 'CCM_SOIL') {
      return []
    }

    const bracketsVariables = this.getBracketsVariables()
    if (
      !bracketsVariables?.possibleRails ||
      !bracketsVariables?.possibleStructures ||
      !bracketsVariables?.needRailsMeters ||
      !bracketsVariables?.needModulesSupportQuantity
    ) {
      return []
    }

    return this.getBracketsCombinations(bracketsVariables)
  }

  getBracketsCombinations(params: BracketVariables) {
    const {
      possibleRails,
      possibleStructures,
      needRailsMeters,
      needModulesSupportQuantity,
    } = params
    const selectedRail = possibleRails.find((rail) => rail.quantity > 0)
    const selectedStructure = possibleStructures.find(
      (structure) => structure.quantity > 0,
    )
    const selectedPanel = this.store.selectSnapshot(
      QuoteMetaState.getSelectedPanel,
    )

    if (possibleRails.length === 0) {
      return this.getOnlyStructuresCombinations({
        possibleStructures,
        needModulesSupportQuantity,
        selectedStructure,
      })
    }
    if (possibleStructures.length === 0) {
      return this.getOnlyRailsCombinations({
        possibleRails,
        needRailsMeters,
        selectedRail,
      })
    }

    if (selectedPanel.quantity === 3) {
      return this.getRailComplementCombination({
        ...params,
        selectedRail,
        selectedStructure,
      })
    }

    return possibleRails
      .map((rail) => {
        return possibleStructures.map((structure) => {
          const railNeedQuantity = Math.ceil(
            needRailsMeters / rail.railsMetersQuantity,
          )
          const structureNeedQuantity = Math.ceil(
            needModulesSupportQuantity / structure.modulesSupportQuantity,
          )
          const isSelected =
            selectedRail &&
            selectedRail.id === rail.id &&
            selectedRail.quantity === railNeedQuantity &&
            selectedStructure &&
            selectedStructure.id === structure.id &&
            selectedStructure.quantity === structureNeedQuantity

          // INFO: BYD2-175
          const bracketMiniRailsKey = 'mini-trilho'
          if (structure.title.toLowerCase().includes(bracketMiniRailsKey)) {
            return {
              title: `${structureNeedQuantity}x ${structure.title}`,
              isSelected,
              content: [
                {
                  id: structure.id,
                  type: ProductTypes.BRACKET,
                  quantity: structureNeedQuantity,
                },
              ],
            }
          }

          return {
            title: `${railNeedQuantity}x ${rail.title}, ${structureNeedQuantity}x ${structure.title}`,
            isSelected,
            content: [
              {
                id: rail.id,
                type: ProductTypes.BRACKET,
                quantity: railNeedQuantity,
              },
              {
                id: structure.id,
                type: ProductTypes.BRACKET,
                quantity: structureNeedQuantity,
              },
            ],
          }
        })
      })
      .flat()
  }

  getOnlyRailsCombinations({
    possibleRails,
    needRailsMeters,
    selectedRail,
  }: OnlyRailsCombinations): any[] {
    return possibleRails.map((rail) => {
      const railNeedQuantity = Math.ceil(
        needRailsMeters / rail.railsMetersQuantity,
      )
      const isSelected =
        selectedRail &&
        selectedRail.id === rail.id &&
        selectedRail.quantity === railNeedQuantity
      const option = {
        title: `${railNeedQuantity}x ${rail.title}`,
        isSelected,
        content: [
          {
            id: rail.id,
            type: ProductTypes.BRACKET,
            quantity: railNeedQuantity,
          },
        ],
      }
      return option
    })
  }

  getOnlyStructuresCombinations({
    possibleStructures,
    needModulesSupportQuantity,
    selectedStructure,
  }: OnlyStructuresCombinations): any[] {
    return possibleStructures.map((structure) => {
      const structureNeedQuantity = Math.ceil(
        needModulesSupportQuantity / structure.modulesSupportQuantity,
      )
      const isSelected =
        selectedStructure &&
        selectedStructure.id === structure.id &&
        selectedStructure.quantity === structureNeedQuantity
      const option = {
        title: `${structureNeedQuantity}x ${structure.title}`,
        isSelected,
        content: [
          {
            id: structure.id,
            type: ProductTypes.BRACKET,
            quantity: structureNeedQuantity,
          },
        ],
      }
      return option
    })
  }

  private getBracketsAmountValues(selectedBrackets: any[]) {
    const onlyWithModulesSupport = selectedBrackets.filter(
      (item) =>
        item?.modulesSupportQuantity && item?.modulesSupportQuantity > 0,
    )
    const totalModulesSupportQuantity =
      onlyWithModulesSupport.reduce(
        (acc, bracket) =>
          acc + bracket.quantity * bracket.modulesSupportQuantity,
        0,
      ) || 0
    const onlyRails = selectedBrackets.filter(
      (item) => item?.railsMetersQuantity && item?.railsMetersQuantity > 0,
    )
    const railsMetersQuantity =
      onlyRails.reduce(
        (acc, bracket) => acc + bracket.quantity * bracket.railsMetersQuantity,
        0,
      ) || 0
    return {
      totalModulesSupportQuantity,
      railsMetersQuantity,
      selectedBrackets,
    }
  }

  /**
   * Filter brackets by roof type and supplier or return all brackets if no filters are set
   * @returns
   */
  private getFilteredBrackets(): any[] {
    const bracketsAtStore = this.store.selectSnapshot(
      QuoteMetaState.getBrackets,
    )
    if (bracketsAtStore.length === 0) {
      return []
    }
    const tabFilters = this.store.selectSnapshot(QuoteMetaState.getTabFilters)
    if (!tabFilters) {
      return []
    }
    const bracketsFilters = tabFilters['brackets']
    const supplierCompatibilities = this.store.selectSnapshot(
      QuoteMetaState.getSupplierCompatibilities,
    )
    const supplier = bracketsFilters?.supplier?.toUpperCase()
    const compatibleSuppliers = supplierCompatibilities
      ? supplierCompatibilities[supplier]
      : []
    const compatibleBrackets = [...bracketsAtStore].filter((bracket) => {
      return compatibleSuppliers.includes(bracket?.supplier?.toUpperCase())
    })
    const filteredBrackets = [...compatibleBrackets].filter((bracket) => {
      return (
        bracketsFilters.roofType === bracket.roofType ||
        bracket.roofType === null
      )
    })
    return filteredBrackets
  }

  /**
   * POLICY: BYD2-779
   * Description: The calculation is based on the spreadsheet in the task description
   *
   */
  createCcmSoilBracketsComposition({
    products,
    panels,
    sizing,
    panelsPerBoard,
  }: CcmSoilBracketsComposition) {
    const productParsed = products.map((product) => ({
      ...product,
      quantity: 0,
    }))
    let composition

    if (sizing === 'A') {
      composition = this.calculateCcmASizing({
        panels,
        products: productParsed,
      })
    }

    if (sizing === 'B') {
      composition = this.calculateCcmBSizing({
        panelsPerBoard,
        products: productParsed,
      })
    }

    composition.forEach((product) => {
      const { item, id, quantity } = product
      this.store.dispatch(
        new UpdateQuoteItemQuantityAction({ item, id, quantity }),
      )
    })
  }

  private calculateCcmBSizing({
    panelsPerBoard,
    products,
  }: CalculateCcmBSizing) {
    const productQuantitys = [...products]

    for (const panels of panelsPerBoard) {
      for (const product of products) {
        const productIndex = products.findIndex(
          (item) => item.sapCode === product.sapCode,
        )
        const quantity = productQuantitys[productIndex].quantity

        if (product.sapCode === 'BR14940522') {
          productQuantitys[productIndex].quantity =
            Math.ceil(panels / 4) + 1 + quantity
        }

        if (product.sapCode === 'BR14940243') {
          productQuantitys[productIndex].quantity =
            (panels !== 0 ? 1 : 0) + quantity
        }

        if (product.sapCode === 'BR14940244') {
          productQuantitys[productIndex].quantity =
            Math.ceil(panels / 4) - 1 + quantity
        }

        if (product.sapCode === 'BR14940523') {
          productQuantitys[productIndex].quantity =
            Math.ceil(panels / 12) + quantity
        }

        if (product.sapCode === 'BR14940524') {
          productQuantitys[productIndex].quantity = panels + quantity
        }
      }
    }

    return productQuantitys
  }

  private calculateCcmASizing({ panels, products }: CalculateCcmASizing) {
    const productQuantitys = [...products]

    let ccmCSupportQuantity = 0

    for (const product of products) {
      const productIndex = products.findIndex(
        (item) => item.sapCode === product.sapCode,
      )

      if (product.sapCode === 'BR14940522') {
        ccmCSupportQuantity = Math.ceil(panels / 4) + 1
        productQuantitys[productIndex].quantity = ccmCSupportQuantity
      }

      if (product.sapCode === 'BR14940243') {
        productQuantitys[productIndex].quantity = 1
      }

      if (product.sapCode === 'BR14940244') {
        const quantity = Math.ceil(panels / 4) - 1
        productQuantitys[productIndex].quantity = quantity < 0 ? 0 : quantity
      }

      if (product.sapCode === 'BR14940523') {
        const quantity = Math.ceil((ccmCSupportQuantity - 1) / 3) + 1
        productQuantitys[productIndex].quantity = quantity
      }

      if (product.sapCode === 'BR14940524') {
        const isOdd = panels % 2 !== 0
        productQuantitys[productIndex].quantity = isOdd ? panels + 1 : panels
      }
    }

    return productQuantitys
  }

  getRailComplementCombination({
    possibleRails,
    possibleStructures,
    needRailsMeters,
    needModulesSupportQuantity,
    selectedRail,
    selectedStructure,
    supportRails,
  }: RailComplementCombination) {
    return possibleRails
      .map((rail) => {
        return possibleStructures.map((structure) => {
          const railNeedQuantity = Math.ceil(
            needRailsMeters / rail.railsMetersQuantity,
          )
          const structureNeedQuantity = Math.ceil(
            needModulesSupportQuantity / structure.modulesSupportQuantity,
          )
          const isSelected =
            selectedRail &&
            selectedRail.id === rail.id &&
            selectedRail.quantity === railNeedQuantity &&
            selectedStructure &&
            selectedStructure.id === structure.id &&
            selectedStructure.quantity === structureNeedQuantity

          // INFO: BYD2-175
          const bracketMiniRailsKey = 'mini-trilho'
          if (structure.title.toLowerCase().includes(bracketMiniRailsKey)) {
            return {
              title: `${structureNeedQuantity}x ${structure.title}`,
              isSelected,
              content: [
                {
                  id: structure.id,
                  type: ProductTypes.BRACKET,
                  quantity: structureNeedQuantity,
                },
              ],
            }
          }

          if (rail.railsMetersQuantity === 2400) {
            return supportRails.map((supportRail) => ({
              title: `${2}x ${rail.title}, ${2}x ${supportRail.title}, ${structureNeedQuantity}x ${structure.title}`,
              isSelected,
              content: [
                {
                  id: rail.id,
                  type: ProductTypes.BRACKET,
                  quantity: 2,
                },
                {
                  id: supportRail.id,
                  type: ProductTypes.BRACKET,
                  quantity: 2,
                },
                {
                  id: structure.id,
                  type: ProductTypes.BRACKET,
                  quantity: structureNeedQuantity,
                },
              ],
            }))
          }

          return {
            title: `${railNeedQuantity}x ${rail.title}, ${structureNeedQuantity}x ${structure.title}`,
            isSelected,
            content: [
              {
                id: rail.id,
                type: ProductTypes.BRACKET,
                quantity: railNeedQuantity,
              },
              {
                id: structure.id,
                type: ProductTypes.BRACKET,
                quantity: structureNeedQuantity,
              },
            ],
          }
        })
      })
      .flat()
  }
}
